/** Defines the storage abstraction and provides a localStorage-backed implementation. */
export interface StorageBackend {
  /** Read a value by key. Returns null if the key does not exist. */
  read(key: string): string | null;
  /** Write a value for the given key, creating or overwriting as needed. */
  write(key: string, value: string): void;
  /** Delete the value at the given key. No-op if the key does not exist. */
  delete(key: string): void;
  /** Check whether a key exists in storage. */
  has(key: string): boolean;
  /** List all keys that start with the given prefix, returning the suffix after the prefix separator. */
  list(prefix: string): string[];
  /** Delete all keys in the namespace except those in the exclusion list. */
  deleteAll(except?: string[]): void;
}

/** StorageBackend implementation backed by the browser's localStorage API. */
export class LocalStorageBackend implements StorageBackend {
  private prefix: string;

  constructor(prefix: string = 'magzigeddon') {
    this.prefix = prefix;
  }

  private prefixed(key: string): string {
    return `${this.prefix}:${key}`;
  }

  /** @inheritDoc */
  read(key: string): string | null {
    return localStorage.getItem(this.prefixed(key));
  }

  /** @inheritDoc */
  write(key: string, value: string): void {
    localStorage.setItem(this.prefixed(key), value);
  }

  /** @inheritDoc */
  delete(key: string): void {
    localStorage.removeItem(this.prefixed(key));
  }

  /** @inheritDoc */
  has(key: string): boolean {
    return localStorage.getItem(this.prefixed(key)) !== null;
  }

  /** @inheritDoc */
  list(prefix: string): string[] {
    const fullPrefix = this.prefixed(prefix) + ':';
    const results: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(fullPrefix)) {
        results.push(key.slice(fullPrefix.length));
      }
    }
    return results;
  }

  /** @inheritDoc */
  deleteAll(except: string[] = []): void {
    const ns = this.prefix + ':';
    const keep = new Set(except.map(k => this.prefixed(k)));
    const toDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(ns) && !keep.has(key)) {
        toDelete.push(key);
      }
    }
    if (toDelete.length > 0) {
      console.log(`[StorageBackend] deleteAll removing ${toDelete.length} key(s): ${toDelete.join(', ')}`);
    }
    for (const key of toDelete) {
      localStorage.removeItem(key);
    }
  }
}
